import { Controller } from "@hotwired/stimulus"
import { Tabs } from "flowbite"

// Persists active tab selection in URL parameters
// Usage: Add data-controller="tabs-persistence" to the tabs container
//        Add data-tabs-persistence-param="tab" to specify the URL param name (default: "tab")
export default class extends Controller {
  static values = {
    param: { type: String, default: "tab" }
  }

  connect() {
    // Listen for tab changes on all tab containers
    this.setupTabChangeListener()

    // Restore tab from URL on page load (delay to let Flowbite initialize)
    setTimeout(() => this.restoreTabFromUrl(), 100)
  }

  restoreTabFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const tabId = urlParams.get(this.paramValue)

    if (tabId) {
      const tabButton = this.element.querySelector(`[data-tabs-target="#${tabId}-panel"]`)

      if (tabButton) {
        // If this tab is inside a hidden panel, we need to show the parent tab first
        const parentPanel = tabButton.closest('[role="tabpanel"]')
        if (parentPanel && parentPanel.classList.contains('hidden')) {
          const parentTabId = parentPanel.id
          const parentTabButton = this.element.querySelector(`[data-tabs-target="#${parentTabId}"]`)
          if (parentTabButton) {
            this.showTab(parentTabButton)
          }
        }

        // Now show the target tab
        this.showTab(tabButton)
      }
    }
  }

  showTab(tabButton) {
    // Find the tabs container this button belongs to
    const tabsContainer = tabButton.closest('[data-tabs-toggle]')
    if (!tabsContainer) return

    const targetSelector = tabButton.dataset.tabsTarget
    const targetPanel = this.element.querySelector(targetSelector)
    if (!targetPanel) return

    // Get classes from this specific tabs container
    const activeClasses = (tabsContainer.dataset.tabsActiveClasses || '').split(' ').filter(Boolean)
    const inactiveClasses = (tabsContainer.dataset.tabsInactiveClasses || '').split(' ').filter(Boolean)

    // Build tab items for Flowbite
    const tabItems = []
    const siblingButtons = tabsContainer.querySelectorAll('[role="tab"]')

    siblingButtons.forEach(btn => {
      const panelSelector = btn.dataset.tabsTarget
      const panel = this.element.querySelector(panelSelector)
      if (panel) {
        tabItems.push({
          id: btn.id,
          triggerEl: btn,
          targetEl: panel
        })
      }
    })

    // Create Flowbite Tabs instance and show the target tab
    const options = {
      defaultTabId: tabButton.id,
      activeClasses: activeClasses.join(' '),
      inactiveClasses: inactiveClasses.join(' ')
    }

    const tabs = new Tabs(tabsContainer, tabItems, options)
    tabs.show(tabButton.id)
  }

  setupTabChangeListener() {
    // Listen for clicks on any tab button within the controller's element
    this.element.addEventListener('click', (event) => {
      const tabButton = event.target.closest('[role="tab"]')
      if (tabButton) {
        const target = tabButton.dataset.tabsTarget
        if (target) {
          // Extract tab ID from target (e.g., "#documents-panel" -> "documents")
          const tabId = target.replace('#', '').replace('-panel', '')
          this.updateUrl(tabId)
        }
      }
    })
  }

  updateUrl(tabId) {
    const url = new URL(window.location)
    url.searchParams.set(this.paramValue, tabId)
    window.history.replaceState({}, '', url)
  }
}
